import React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import FavoriteProductEditItem from "@/features/my-account/favorite-product-edit-item";
import useGetSearchedSkus from "@/features/my-account/hooks/useGetSearchedSkus";
import { useFormikContext } from "formik";
import {
  FavoriteProductFormikValues,
  FavSkuItem,
  SearchedSkuItem,
} from "@/features/my-account/types";
import NoResultCustom from "@/shared/components/no-result-simple";
import SVG from "react-inlinesvg";

interface Props {
  value: string;
  buyer_id: number;
}

function SearchedSkuList({ value, buyer_id }: Props) {
  const { searchedSkus } = useGetSearchedSkus(value, buyer_id);
  const { setFieldValue, values } =
    useFormikContext<FavoriteProductFormikValues>();

  const { favoriteProducts, requestSku } = values;

  // 요청한 SKU가 있으면 그걸, 없으면 API 결과를 currentSkuList로
  const requestedSkus: SearchedSkuItem[] = [
    {
      sku: value,
      image_urls: ["/icons-v1_1/null.png"],
      brand_name: "",
      id: 0,
      sku_in_pathrade: false,
      added: true,
    },
  ];
  const currentSkuList =
    requestSku.length > 0 &&
    requestSku.includes(value) &&
    searchedSkus.length === 0
      ? requestedSkus
      : searchedSkus;

  // 1) 스크롤 컨테이너 ref
  const parentRef = React.useRef<HTMLDivElement>(null);

  // 2) useVirtualizer 세팅
  const rowVirtualizer = useVirtualizer({
    count: currentSkuList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 76, // 한 아이템 높이 대략값(px)
    overscan: 5, // 위/아래 여분으로 몇 개 더 렌더링할지
  });

  // 3) 가상화된 아이템 배열
  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <>
      <Flex gap="4px">
        <Text textStyle="body-md-regular" color="#6F6F6C">
          검색 결과
        </Text>
        <Text textStyle="body-md-bold" color="#1272EF">
          {currentSkuList.length}
        </Text>
      </Flex>

      {/* 4) 가상화된 스크롤 박스 */}
      <Box
        ref={parentRef}
        overflow="auto"
        height="358px"
        padding="12px"
        borderRadius="8px"
        border="1px solid #D4D4D1"
        position="relative" /* 필요 */
      >
        {/* 전체 높이 만큼 빈 공간 */}
        <Box
          height={`${rowVirtualizer.getTotalSize()}px`}
          width="100%"
          position="relative"
        >
          {virtualItems.map(({ index, start, key }) => {
            const rowItem = currentSkuList[index];
            const { sku, sku_in_pathrade, id, image_urls, brand_name } =
              rowItem;
            const isRequested = requestSku.includes(sku);
            const matches = sku_in_pathrade
              ? (item: FavSkuItem) => item.id === id
              : (item: FavSkuItem) => item.sku === sku;
            const isFavorite = favoriteProducts.some(matches);

            return (
              <Box
                key={key.toString()}
                position="absolute"
                top={0}
                left={0}
                width="100%"
                style={{ transform: `translateY(${start}px)` }}
              >
                <FavoriteProductEditItem
                  src={image_urls?.at(0) ?? "/icons-v1_1/null.png"}
                  sku={sku}
                  value={value}
                  brandName={brand_name}
                  added={favoriteProducts.find(matches)?.added}
                  onClick={() => {
                    if (isRequested) {
                      setFieldValue(
                        "requestSku",
                        requestSku.filter((reqSku) => reqSku !== sku)
                      );
                      setFieldValue(
                        "favoriteProducts",
                        favoriteProducts.filter((prod) => !matches(prod))
                      );
                      return;
                    }

                    const newFavs = isFavorite
                      ? favoriteProducts.map((prod) =>
                          matches(prod) ? { ...prod, added: !prod.added } : prod
                        )
                      : [...favoriteProducts, { ...rowItem, added: true }];

                    setFieldValue("favoriteProducts", newFavs);
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* 빈 결과 처리는 여기 그대로 유지 */}
        {currentSkuList.length === 0 && (
          <NoResultCustom text="검색 결과가 없습니다." size="28px">
            <Button
              display="flex"
              gap="4px"
              alignItems="center"
              variant="ghost"
              onClick={() => {
                setFieldValue("requestSku", [...values.requestSku, value]);
                setFieldValue("favoriteProducts", [
                  ...values.favoriteProducts,
                  requestedSkus[0],
                ]);
              }}
            >
              <Box color="#8F8F8C">
                <SVG src="/icons-v1_1/plus.svg" width="16px" height="16px" />
              </Box>
              <Text textStyle="body-sm-bold" color="#444444">
                {`'${value}' 추가하기`}
              </Text>
            </Button>
          </NoResultCustom>
        )}
      </Box>
    </>
  );
}

export default SearchedSkuList;
